// Store OS's own screens on a server with several businesses, before anyone is signed in: sign in
// (and pick the business when one email works at more than one), set up a store tablet with an
// email and code, a forgotten password, and signup: pick a plan, pay on Stripe's page, and come
// back to a business of your own. Nothing here belongs to any one business.
import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, get, post } from '../lib/api';
import { navigate, storageGet, storageSet, useRoute } from '../lib/hooks';
import { useSession } from '../lib/session';
import { Badge, Button, Field, PinPad, Spinner } from '../ui/kit';
import { Icon } from '../ui/icons';
import { StoreOsLogo } from '../ui/brand';
import { formatCents } from '../../shared/money';
import { FEATURE_LABELS, MAX_LOCATIONS, PLANS, PLAN_IDS, monthlyTotal, usersAllowed, type PlanId } from '../../shared/plans';
import { INDUSTRIES, INDUSTRY_IDS, type IndustryId } from '../../shared/industries';

const TAGLINE = 'The Operating System for Your Store.';
const SAVED_EMAIL = 'storeos.setupEmail';

function Logo() {
  return (
    <div className="gate-brand">
      <StoreOsLogo />
      <div className="gate-os"><span>{TAGLINE}</span></div>
    </div>
  );
}

/** Which of the signed-out screens this address shows. */
export function PlatformGate() {
  const r = useRoute();
  const [a, b] = r.parts;
  if (a === 'signup' && b === 'done') return <SignupDoneScreen />;
  if (a === 'signup') return <SignupScreen query={r.query} />;
  if (a === 'reset' && b) return <ResetPasswordScreen token={b} />;
  if (a === 'forgot') return <ForgotPasswordScreen />;
  if (a === 'tablet') return <LobbyTabletScreen />;
  return <LobbySignInScreen />;
}

interface Choice { id: string; name: string }

/** More than one business for this email: which one now (asked only after the password or code matched). */
function BusinessPicker({ choices, onPick, busy }: { choices: Choice[]; onPick: (id: string) => void; busy: boolean }) {
  return (
    <div className="stack">
      <div>
        <h1>Which business?</h1>
        <p className="muted">Your email works at more than one business on Store OS. Pick the one to open now.</p>
      </div>
      <div className="list">
        {choices.map((c) => (
          <button key={c.id} type="button" className="list-row" disabled={busy} onClick={() => onPick(c.id)}>
            <Icon.home />
            <span className="main-col t strong">{c.name}</span>
            <Icon.chevronRight />
          </button>
        ))}
      </div>
    </div>
  );
}

/** Email and password, for owners and managers of any business on this server. */
export function LobbySignInScreen() {
  const { refresh, status } = useSession();
  const [email, setEmail] = useState(() => storageGet<string>(SAVED_EMAIL, ''));
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [choices, setChoices] = useState<Choice[] | null>(null);
  const signIn = async (businessId?: string) => {
    setBusy(true);
    setError(null);
    try {
      const r = await post<{ ok?: boolean; choose?: Choice[] }>('/api/auth/login', { email: email.trim(), password, businessId: businessId ?? null });
      if (r.choose) {
        setChoices(r.choose);
        return;
      }
      storageSet(SAVED_EMAIL, email.trim().toLowerCase());
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
      setChoices(null);
    } finally {
      setBusy(false);
    }
  };
  const signup = status?.platform?.signup;
  return (
    <main className="gate">
      <div className="gate-card">
        <Logo />
        {choices ? (
          <>
            <BusinessPicker choices={choices} busy={busy} onPick={(id) => signIn(id)} />
            {error ? <div className="error-text" role="alert">{error}</div> : null}
            <Button variant="ghost" onClick={() => { setChoices(null); setPassword(''); }}>Back</Button>
          </>
        ) : (
          <form className="stack" onSubmit={(e) => { e.preventDefault(); signIn(); }}>
            <div>
              <h1>Sign in</h1>
              <p className="muted">Owners and managers sign in with email. Store staff use their code on the store’s tablet.</p>
            </div>
            <Field label="Email"><input className="input input-lg" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" autoFocus={!email} /></Field>
            <Field label="Password"><input className="input input-lg" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" autoFocus={!!email} /></Field>
            {error ? <div className="error-text" role="alert">{error}</div> : null}
            <Button type="submit" variant="primary" size="lg" block busy={busy}>Sign in</Button>
            <div className="row-wrap" style={{ justifyContent: 'space-between' }}>
              <a className="link-btn" href="#/forgot">Forgot your password?</a>
              <a className="link-btn" href="#/tablet">Set up a store tablet with your code</a>
            </div>
          </form>
        )}
        {signup?.open ? (
          <div className="card card-tight spread wrap-sm">
            <span><span className="strong">New to Store OS?</span> <span className="muted">Pick a plan and set up your shop.</span></span>
            <Button variant="secondary" onClick={() => navigate('/signup')}>Start your subscription</Button>
          </div>
        ) : null}
      </div>
    </main>
  );
}

/** A new device at a business: the owner's or a manager's email and their code make it that store's tablet. */
export function LobbyTabletScreen() {
  const { refresh } = useSession();
  const saved = storageGet<string>(SAVED_EMAIL, '');
  const [email, setEmail] = useState(saved);
  const [editEmail, setEditEmail] = useState(!saved);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [choices, setChoices] = useState<Choice[] | null>(null);
  const [stores, setStores] = useState<{ id: string; name: string }[] | null>(null);
  const [store, setStore] = useState('');
  const [businessId, setBusinessId] = useState<string | null>(null);
  const pin = useRef('');
  const setUp = async (code: string, opts: { businessId?: string | null; locationId?: string } = {}) => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setEditEmail(true);
      return setError('Enter your email first.');
    }
    pin.current = code;
    setBusy(true);
    setError(null);
    try {
      const r = await post<{ ok?: boolean; choose?: Choice[] }>('/api/auth/tablet-setup', {
        email: email.trim(), pin: code, businessId: opts.businessId ?? businessId, locationId: opts.locationId ?? (store || undefined),
      });
      if (r.choose) {
        setChoices(r.choose);
        return;
      }
      storageSet(SAVED_EMAIL, email.trim().toLowerCase());
      await refresh();
      navigate('/');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'pick_store' && Array.isArray(err.details?.stores)) {
        setStores(err.details.stores);
        setError(null);
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not set up this tablet.');
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="gate">
      <div className="gate-card" style={{ maxWidth: 460 }}>
        <Logo />
        {choices ? (
          <BusinessPicker choices={choices} busy={busy} onPick={(id) => { setBusinessId(id); setChoices(null); setUp(pin.current, { businessId: id }); }} />
        ) : stores ? (
          <div className="stack">
            <div><h1>Which store is this tablet for?</h1><p className="muted">It stays that store’s tablet until the owner removes it in Settings.</p></div>
            <div className="list">
              {stores.map((s) => (
                <button key={s.id} type="button" className="list-row" disabled={busy} onClick={() => { setStore(s.id); setUp(pin.current, { locationId: s.id }); }}>
                  <Icon.home /><span className="main-col t strong">{s.name}</span><Icon.chevronRight />
                </button>
              ))}
            </div>
            {error ? <div className="error-text" role="alert">{error}</div> : null}
          </div>
        ) : (
          <>
            <div style={{ textAlign: 'center' }}>
              <h1>Set up this tablet</h1>
              <p className="muted">Owner or manager: your email, then your 4-digit code.</p>
            </div>
            {editEmail ? (
              <Field label="Email"><input className="input input-lg" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(null); }} autoComplete="username" autoFocus inputMode="email" /></Field>
            ) : (
              <div className="spread card card-tight"><span className="strong">{email}</span><button type="button" className="link-btn" onClick={() => setEditEmail(true)}>Change</button></div>
            )}
            <PinPad onSubmit={(code) => setUp(code)} busy={busy} error={error} autoFocus={!editEmail} />
          </>
        )}
        <div className="row" style={{ justifyContent: 'center' }}>
          <a className="link-btn" href="#/">Sign in with a password instead</a>
        </div>
      </div>
    </main>
  );
}

export function ForgotPasswordScreen() {
  const [email, setEmail] = useState(() => storageGet<string>(SAVED_EMAIL, ''));
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post('/api/platform/password/forgot', { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t go through. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="gate">
      <form className="gate-card" onSubmit={submit}>
        <Logo />
        {sent ? (
          <div className="stack">
            <h1>Check your email</h1>
            <p className="muted">If {email.trim()} has a Store OS account, a link to choose a new password is on its way. It works for an hour.</p>
            <a className="link-btn" href="#/">Back to sign in</a>
          </div>
        ) : (
          <>
            <div>
              <h1>Forgot your password?</h1>
              <p className="muted">Type the email you sign in with. We’ll send a link to choose a new password.</p>
            </div>
            <Field label="Email"><input className="input input-lg" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="username" autoFocus /></Field>
            {error ? <div className="error-text" role="alert">{error}</div> : null}
            <Button type="submit" variant="primary" size="lg" block busy={busy}>Send the link</Button>
            <a className="link-btn" href="#/" style={{ textAlign: 'center' }}>Back to sign in</a>
          </>
        )}
      </form>
    </main>
  );
}

export function ResetPasswordScreen({ token }: { token: string }) {
  const [password, setPassword] = useState('');
  const [again, setAgain] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== again) return setError('The two passwords aren’t the same.');
    setBusy(true);
    setError(null);
    try {
      await post('/api/platform/password/reset', { token, password });
      setDone(true);
      // The link is used: it shouldn't stay in the address bar or the history.
      history.replaceState(null, '', '/#/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t go through. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="gate">
      <form className="gate-card" onSubmit={submit}>
        <Logo />
        {done ? (
          <div className="stack">
            <h1>Password saved</h1>
            <p className="muted">Sign in with your new password. Anywhere you were signed in with the old one is signed out.</p>
            <Button variant="primary" size="lg" block onClick={() => navigate('/')}>Sign in</Button>
          </div>
        ) : (
          <>
            <div><h1>Choose a new password</h1><p className="muted">10 characters or more. A few words you’ll remember work well.</p></div>
            <Field label="New password"><input className="input input-lg" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={10} autoComplete="new-password" autoFocus /></Field>
            <Field label="Type it again"><input className="input input-lg" type="password" value={again} onChange={(e) => setAgain(e.target.value)} required minLength={10} autoComplete="new-password" /></Field>
            {error ? <div className="error-text" role="alert">{error}</div> : null}
            <Button type="submit" variant="primary" size="lg" block busy={busy}>Save the new password</Button>
          </>
        )}
      </form>
    </main>
  );
}

// ---------------------------------------------------------------- signup

interface PlansInfo {
  signup: { open: boolean; reason: string | null; test_mode: boolean };
  max_locations: number;
  legal?: { terms_url: string | null; privacy_url: string | null };
}

const US_ZONES: [string, string][] = [
  ['America/New_York', 'Eastern'], ['America/Chicago', 'Central'], ['America/Denver', 'Mountain'], ['America/Phoenix', 'Arizona'],
  ['America/Los_Angeles', 'Pacific'], ['America/Anchorage', 'Alaska'], ['Pacific/Honolulu', 'Hawaii'],
];

function localZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
  } catch {
    return 'America/Los_Angeles';
  }
}

export function SignupScreen({ query }: { query: URLSearchParams }) {
  const [info, setInfo] = useState<PlansInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const start = query.get('plan');
  const [plan, setPlan] = useState<PlanId>(start === 'starter' || start === 'business' ? start : 'growth');
  const [locations, setLocations] = useState(1);
  const [industry, setIndustry] = useState<IndustryId>(() => {
    const k = query.get('industry');
    return k === 'computer' || k === 'auto' || k === 'vacuum' ? k : 'cellphone';
  });
  const zone = localZone();
  const [f, setF] = useState({ businessName: '', firstName: '', lastName: '', email: '', password: '', timezone: zone });
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const canceled = query.get('canceled') === '1';
  useEffect(() => {
    get<PlansInfo>('/api/platform/plans').then(setInfo).catch((e) => setLoadError(e instanceof Error ? e.message : 'Store OS didn’t answer.'));
  }, []);
  const zones = useMemo(() => (US_ZONES.some(([z]) => z === zone) ? US_ZONES : [[zone, zone.replace(/_/g, ' ')] as [string, string], ...US_ZONES]), [zone]);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const total = monthlyTotal(plan, locations);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agree) return setError('Check the box to agree to the terms first.');
    setBusy(true);
    setError(null);
    try {
      const r = await post<{ checkout_url: string }>('/api/platform/signup', { ...f, plan, locations, industry, agree: true });
      // Stripe's own checkout page takes the card; this page never sees it.
      window.location.assign(r.checkout_url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t go through. Try again.');
      setBusy(false);
    }
  };
  if (!info && !loadError) return <div className="gate"><Spinner label="Loading the plans" /></div>;
  const open = !!info?.signup.open;
  return (
    <main className="gate gate-scroll">
      <form className="gate-card signup-card" onSubmit={submit}>
        <Logo />
        <div>
          <h1>Start your Store OS subscription</h1>
          <p className="muted">Every plan has all four kinds of shop, sales, repairs or work orders, inventory, customers and daily reports. Billed monthly per location. Cancel any time from Billing.</p>
        </div>
        {canceled ? <div className="banner"><Icon.info /><div>Checkout was closed before paying, so nothing was charged. Pick up where you left off below.</div></div> : null}
        {!open ? (
          <div className="banner warn"><Icon.info /><div><div className="strong">Online signup isn’t open yet.</div><div className="small">{info?.signup.reason ?? loadError ?? ''} Try the demo, or contact Store OS to get started.</div></div></div>
        ) : info?.signup.test_mode ? (
          <div className="banner"><Icon.info /><div><div className="strong">Test mode: no real charges.</div><div className="small">On Stripe’s page use the test card 4242 4242 4242 4242, any future date and any 3 digits.</div></div></div>
        ) : null}

        <fieldset className="stack" disabled={!open || busy}>
          <legend className="h3">1. Plan</legend>
          <div className="plan-grid" role="radiogroup" aria-label="Plan">
            {PLAN_IDS.map((id) => {
              const p = PLANS[id];
              return (
                <label key={id} className={`plan-card${plan === id ? ' on' : ''}`}>
                  <input type="radio" name="plan" value={id} checked={plan === id} onChange={() => setPlan(id)} />
                  <span className="plan-name">{p.name}</span>
                  <span className="plan-price">{formatCents(p.price_cents)}<span className="small muted"> /location /month</span></span>
                  <span className="small">{p.users_per_location} named users per location (the owner counts)</span>
                  <ul className="plan-feats small">
                    {p.features.length ? p.features.map((x) => <li key={x}>{FEATURE_LABELS[x]}{x === 'buybacks' ? ' (phone shops)' : ''}</li>) : <li>Everything every plan has</li>}
                  </ul>
                </label>
              );
            })}
          </div>
          <div className="form-grid">
            <Field label="Locations" hint={`1 to ${info?.max_locations ?? MAX_LOCATIONS}; each is billed at the plan’s price`}>
              <input className="input" type="number" min={1} max={info?.max_locations ?? MAX_LOCATIONS} value={locations} onChange={(e) => setLocations(Math.max(1, Math.min(info?.max_locations ?? MAX_LOCATIONS, Math.floor(Number(e.target.value) || 1))))} />
            </Field>
            <Field label="Monthly total">
              <div className="input readonly strong" aria-live="polite">{formatCents(total)} <span className="small muted">· {usersAllowed(plan, locations)} named users</span></div>
            </Field>
          </div>
        </fieldset>

        <fieldset className="stack" disabled={!open || busy}>
          <legend className="h3">2. Your shop</legend>
          <div className="form-grid">
            <Field label="Business name" className="full"><input className="input" value={f.businessName} onChange={set('businessName')} required minLength={2} maxLength={80} autoComplete="organization" /></Field>
            <Field label="Kind of shop" hint={INDUSTRIES[industry].job === 'work order' ? 'work orders for vehicles' : `repair tickets for each ${INDUSTRIES[industry].item}`}>
              <select className="select" value={industry} onChange={(e) => setIndustry(e.target.value as IndustryId)}>
                {INDUSTRY_IDS.map((id) => <option key={id} value={id}>{INDUSTRIES[id].name}</option>)}
              </select>
            </Field>
            <Field label="Time zone">
              <select className="select" value={f.timezone} onChange={set('timezone')}>
                {zones.map(([z, label]) => <option key={z} value={z}>{label}</option>)}
              </select>
            </Field>
          </div>
        </fieldset>

        <fieldset className="stack" disabled={!open || busy}>
          <legend className="h3">3. The owner’s account</legend>
          <div className="form-grid">
            <Field label="First name"><input className="input" value={f.firstName} onChange={set('firstName')} required autoComplete="given-name" /></Field>
            <Field label="Last name"><input className="input" value={f.lastName} onChange={set('lastName')} autoComplete="family-name" /></Field>
            <Field label="Email" hint="you sign in with it" className="full"><input className="input" type="email" value={f.email} onChange={set('email')} required autoComplete="email" /></Field>
            <Field label="Password" hint="10+ characters" className="full"><input className="input" type="password" value={f.password} onChange={set('password')} required minLength={10} autoComplete="new-password" /></Field>
          </div>
          <label className="check">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
            <span>
              I agree to the Store OS{' '}
              {info?.legal?.terms_url ? <a href={info.legal.terms_url} target="_blank" rel="noreferrer">terms</a> : 'terms'}
              {' '}and{' '}
              {info?.legal?.privacy_url ? <a href={info.legal.privacy_url} target="_blank" rel="noreferrer">privacy policy</a> : 'privacy policy'}
              , and to {formatCents(total)} a month ({PLANS[plan].name}, {locations} location{locations === 1 ? '' : 's'}) until I cancel.
            </span>
          </label>
        </fieldset>
        {error ? <div className="error-text" role="alert">{error}</div> : null}
        <Button type="submit" variant="primary" size="lg" block busy={busy} disabled={!open}>Continue to payment</Button>
        <p className="small muted" style={{ textAlign: 'center' }}>You pay on Stripe’s secure page. Your shop is made when Stripe confirms the payment.</p>
        <a className="link-btn" href="#/" style={{ textAlign: 'center' }}>Already have an account? Sign in</a>
      </form>
    </main>
  );
}

/** Back from Stripe: wait for Stripe to confirm the payment (checked on the server), then open the new shop. */
export function SignupDoneScreen() {
  const { refresh } = useSession();
  const [state, setState] = useState<{ status: string; business: string | null; message: string | null; signed_in: boolean } | null>(null);
  const [tries, setTries] = useState(0);
  useEffect(() => {
    let live = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const check = async () => {
      try {
        const s = await get<{ status: string; business: string | null; message: string | null; signed_in: boolean }>('/api/platform/signup/status');
        if (!live) return;
        setState(s);
        if (s.status === 'ready' && s.signed_in) {
          history.replaceState(null, '', '/#/');
          await refresh();
          return;
        }
        if (s.status === 'waiting') {
          setTries((n) => n + 1);
          timer = setTimeout(check, 2_500);
        }
      } catch {
        if (live) timer = setTimeout(check, 5_000);
      }
    };
    check();
    return () => { live = false; if (timer) clearTimeout(timer); };
  }, [refresh]);
  const status = state?.status ?? 'waiting';
  return (
    <main className="gate">
      <div className="gate-card">
        <Logo />
        {status === 'waiting' ? (
          <div className="stack" style={{ textAlign: 'center' }}>
            <Spinner label="Waiting for Stripe to confirm the payment" />
            <h1>Setting up your shop</h1>
            <p className="muted">{tries > 24 ? 'This is taking longer than usual. You can leave this page: we’ll email you when your shop is ready, and you can sign in with your email and password.' : 'This takes a few seconds once Stripe confirms the payment.'}</p>
          </div>
        ) : status === 'ready' ? (
          <div className="stack" style={{ textAlign: 'center' }}>
            <h1>{state?.business ?? 'Your shop'} is ready</h1>
            <p className="muted">Sign in with the email and password you chose.</p>
            <Button variant="primary" size="lg" block onClick={() => navigate('/')}>Sign in</Button>
          </div>
        ) : status === 'none' ? (
          <div className="stack" style={{ textAlign: 'center' }}>
            <h1>Nothing to finish here</h1>
            <p className="muted">This browser has no signup in progress. If you already paid, sign in with your email and password.</p>
            <Button variant="primary" size="lg" block onClick={() => navigate('/')}>Sign in</Button>
            <a className="link-btn" href="#/signup">Start a subscription</a>
          </div>
        ) : (
          <div className="stack" style={{ textAlign: 'center' }}>
            <h1>{status === 'failed' ? 'The payment didn’t go through' : 'The checkout wasn’t finished'}</h1>
            <p className="muted">{state?.message ?? ''} Nothing was set up and nothing was charged.</p>
            <Button variant="primary" size="lg" block onClick={() => navigate('/signup')}>Try again</Button>
          </div>
        )}
        <div style={{ textAlign: 'center' }}><Badge tone="blue" icon="lock">Payments by Stripe</Badge></div>
      </div>
    </main>
  );
}
